import React, { useCallback, useState } from 'react';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import { GoogleMap, MarkerF, InfoWindowF, useJsApiLoader } from '@react-google-maps/api';
import { PinChip } from '../MessageProfile';
import RoomOutlinedIcon from '@mui/icons-material/RoomOutlined';
import { mapLibs } from '../map/Map';
import { GetUserTypePinPNG } from '../markers/MMMarkers';
import type { StoryData } from './RandomPatientStory';

interface OnboardingMiniMapProps {
  story: StoryData | null;
}

const containerStyle = {
  width: '100%',
  height: '100%',
  borderRadius: '12px',
};

export const OnboardingMiniMap: React.FC<OnboardingMiniMapProps> = ({ story }) => {
  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: 'AIzaSyDvqapzpG_NMGMUv9Nj9IJnLNasbt_XzR8',
    libraries: mapLibs,
  });

  const [infoOpen, setInfoOpen] = useState(true);

  const onMapLoad = useCallback((map: google.maps.Map) => {
    map.setOptions({ motionTrackingControl: false } as any);
  }, []);

  const glassySx = {
    borderRadius: 3,
    backgroundColor: 'rgba(244, 240, 255, 0.55)',
    backdropFilter: 'blur(24px)',
    WebkitBackdropFilter: 'blur(24px)',
    border: 'none',
    boxShadow: '0 2px 12px rgba(80, 60, 160, 0.06), inset 0 0 0 1px rgba(164, 142, 233, 0.18)',
    overflow: 'hidden',
  };

  if (!story || story.lat == null || story.lng == null) {
    return (
      <Box sx={{ ...glassySx, p: 2.5 }}>
        <Stack gap={1.5}>
          <Skeleton animation="wave" variant="rounded" width="100%" height={200} sx={{ borderRadius: 2 }} />
          <Skeleton animation="wave" variant="rounded" width="60%" height={16} />
        </Stack>
      </Box>
    );
  }

  if (!isLoaded) {
    return (
      <Box sx={{ ...glassySx, p: 2.5 }}>
        <Skeleton animation="wave" variant="rounded" width="100%" height={240} sx={{ borderRadius: 2 }} />
      </Box>
    );
  }

  const mapOptions: google.maps.MapOptions = {
    streetViewControl: false,
    fullscreenControl: false,
    mapTypeControl: false,
    zoomControl: false,
    rotateControl: false,
    panControl: false,
    scaleControl: false,
    gestureHandling: 'none',
    disableDefaultUI: true,
    clickableIcons: false,
    mapId: 'b5fcf45ccbb2b48',
  };

  const center = { lat: story.lat, lng: story.lng };
  const iconUrl = GetUserTypePinPNG(story.type);

  return (
    <Box sx={glassySx}>
      <Box sx={{ height: 260, position: 'relative' }}>
        <GoogleMap
          mapContainerStyle={containerStyle}
          center={center}
          zoom={7}
          onLoad={onMapLoad}
          options={mapOptions}
        >
          <MarkerF
            position={center}
            icon={iconUrl ? {
              url: iconUrl as string,
              scaledSize: new window.google.maps.Size(40, 40),
            } : undefined}
            onClick={() => setInfoOpen(true)}
          >
            {infoOpen && (
              <InfoWindowF
                position={center}
                options={{ disableAutoPan: true }}
                onCloseClick={() => setInfoOpen(false)}
              >
                <MiniInfoWindow story={story} />
              </InfoWindowF>
            )}
          </MarkerF>
        </GoogleMap>
      </Box>
    </Box>
  );
};

const MiniInfoWindow: React.FC<{ story: StoryData }> = ({ story }) => (
  <Stack direction="column" sx={{
    width: '240px',
    maxHeight: '280px',
    overflow: 'hidden',
    borderRadius: '12px',
    background: 'linear-gradient(145deg, rgba(244, 240, 255, 0.97) 0%, rgba(236, 232, 250, 0.95) 50%, rgba(244, 240, 255, 0.97) 100%)',
    border: 'none',
    boxShadow: '0 8px 32px rgba(101, 110, 255, 0.12), 0 2px 8px rgba(0,0,0,0.06), inset 0 0 0 1px rgba(164, 142, 233, 0.22), inset 0 1px 0 rgba(255,255,255,0.6)',
  }}>
    {/* Header */}
    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{
      background: 'linear-gradient(135deg, rgba(139,92,246,0.85) 0%, rgba(165,120,255,0.80) 100%)',
      px: 1.5, py: 1, flexShrink: 0, borderRadius: '12px 12px 0 0',
      boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.3)',
    }}>
      <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '0.85rem', lineHeight: 1.3 }}>
        {story.name_public}
      </Typography>
    </Stack>

    {/* Badge + location */}
    <Stack direction="column" gap={0.5} sx={{ px: 1.5, pt: 1, pb: 0.5, flexShrink: 0 }}>
      <Stack direction="row" gap={1} alignItems="center" sx={{ overflow: 'hidden' }}>
        <PinChip category="User" type={story.type} size="small" iconSizePx={14} sx={{ height: '22px', fontSize: '0.65rem', flexShrink: 0 }} />
        {story.formattedsearchterm && (
          <Stack direction="row" gap={0.5} alignItems="flex-start" sx={{ minWidth: 0, overflow: 'hidden' }}>
            <RoomOutlinedIcon sx={{ fontSize: '0.8rem', color: 'primary.main', flexShrink: 0, mt: '1px' }} />
            <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.3, fontSize: 11 }}>{story.formattedsearchterm}</Typography>
          </Stack>
        )}
      </Stack>
    </Stack>

    {/* Story excerpt */}
    <Stack direction="column" gap={0.5} sx={{ px: 1.5, pb: 1, overflow: 'auto', flexGrow: 1 }}>
      {story.comment && (
        <Box sx={{ borderLeft: '3px solid #E0DEFF', pl: 1.5, py: 0.5, backgroundColor: '#FAFAFA', borderRadius: 1 }}>
          <Typography variant="body2" sx={{
            color: '#555', lineHeight: 1.5, fontSize: 12,
            display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden',
          }}>
            {story.comment}
          </Typography>
        </Box>
      )}
    </Stack>
  </Stack>
);
